import {Badge, Button} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconPlayerPlay, IconPlayerStop, IconTrash} from '@tabler/icons-react';
import React, {useState} from 'react';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {
  closeOfflineSession,
  discardOfflineSession,
  startOfflineSession,
} from 'src/offline/offlineSessionManager';
import type {OfflineSession, OutboxItem} from 'src/offline/types';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useAppSelector} from 'src/redux/hooks';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {OfflineSnapshotSummary} from '../hooks/useOfflineTestData';

export interface SessionCardProps {
  activeSession: OfflineSession | undefined;
  outboxItems: OutboxItem[];
  snapshots: OfflineSnapshotSummary[];
}

export const SessionCard: React.FC<SessionCardProps> = ({
  activeSession,
  outboxItems,
  snapshots,
}) => {
  const {isOffline} = useConnectivity();
  const {setAlert} = useAlert();
  const instanceId = useAppSelector(state => state.session.instance?.id);
  const userId = useAppSelector(state => state.session.user?.id);

  const [isStarting, setIsStarting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);

  const outstandingCount = outboxItems.filter(
    item =>
      item.status === 'pending' ||
      item.status === 'failed' ||
      item.status === 'syncing'
  ).length;
  const isFullySynced = outstandingCount === 0;

  const handleStart = async () => {
    if (instanceId === undefined || userId === undefined) {
      setAlert(
        'Missing current instance/user — cannot start a session.',
        'error'
      );
      return;
    }
    setIsStarting(true);
    try {
      await startOfflineSession();
      setAlert('Offline session started.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsStarting(false);
    }
  };

  const handleClose = async () => {
    if (!activeSession) return;
    setIsClosing(true);
    try {
      await closeOfflineSession(activeSession.id);
      setAlert('Offline session closed.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsClosing(false);
    }
  };

  const handleDiscard = async () => {
    if (!activeSession) return;
    setIsDiscarding(true);
    try {
      await discardOfflineSession(activeSession.id);
      setAlert('Offline session discarded.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsDiscarding(false);
      setDiscardModalOpen(false);
    }
  };

  return (
    <SectionCard title="Session">
      <Stack p="md" gap="sm">
        {activeSession ? (
          <>
            <Group gap="xl">
              <Text size="sm">
                <Text span fw={500}>
                  Session ID:
                </Text>{' '}
                {activeSession.id}
              </Text>
              <Text size="sm">
                <Text span fw={500}>
                  Instance:
                </Text>{' '}
                {activeSession.instanceId}
              </Text>
              <Text size="sm">
                <Text span fw={500}>
                  Started:
                </Text>{' '}
                {new Date(activeSession.startedAt).toLocaleString()}
              </Text>
            </Group>
            {activeSession.contextKey && (
              <Text size="sm">
                <Text span fw={500}>
                  Context:
                </Text>{' '}
                {activeSession.contextKey}
              </Text>
            )}
            <Group gap="xs">
              <Badge color="gray" variant="light">
                {outboxItems.length} item{outboxItems.length === 1 ? '' : 's'}
              </Badge>
              <Badge color={isFullySynced ? 'green' : 'yellow'} variant="light">
                {outstandingCount} pending/failed/syncing
              </Badge>
              <Badge
                color={activeSession.ready ? 'green' : 'gray'}
                variant="light">
                {activeSession.ready ? 'ready' : 'hydrating'}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Hydration snapshots:{' '}
              {snapshots.length === 0
                ? 'none'
                : snapshots
                    .map(
                      snapshot =>
                        `${snapshot.key} (${new Date(snapshot.cachedAt).toLocaleTimeString()})`
                    )
                    .join(', ')}
            </Text>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            No active offline session.
          </Text>
        )}
        <Group gap="sm">
          <Button
            size="sm"
            leftSection={<IconPlayerPlay size={16} />}
            onClick={handleStart}
            loading={isStarting}
            disabled={!!activeSession || isOffline}>
            Start Session
          </Button>
          <Button
            size="sm"
            variant="subtle"
            leftSection={<IconPlayerStop size={16} />}
            onClick={handleClose}
            loading={isClosing}
            disabled={!activeSession || !isFullySynced}>
            Close Session
          </Button>
          <Button
            size="sm"
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={() => setDiscardModalOpen(true)}
            disabled={!activeSession}>
            Discard Session
          </Button>
        </Group>
      </Stack>
      <ConfirmDialog
        opened={discardModalOpen}
        onClose={() => setDiscardModalOpen(false)}
        onConfirm={handleDiscard}
        title="Discard offline session?"
        body="This permanently deletes the session, its outbox items, and cached snapshots. This cannot be undone."
        confirmationButtonColor="red"
        buttonTextYes="Discard"
        buttonTextNo="Cancel"
        confirmationButtonDisabled={isDiscarding}
      />
    </SectionCard>
  );
};
